import React, { useState, useEffect, useRef } from 'react';
import { 
  Activity, AlertTriangle, AlertCircle, CheckCircle, TrendingUp, 
  TrendingDown, Users, Lock, Unlock, Bot, Search, Filter, ChevronLeft, 
  ChevronRight, Zap, Shield, DollarSign, Eye, Flame, Droplet, Clock 
} from 'lucide-react';
import styles from './LiveMonitor.module.sass';

const LiveMonitor = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Risk feed auto-scroll ref
  const riskFeedRef = useRef(null);
  
  // Simulated real-time risk events
  const [riskEvents, setRiskEvents] = useState([
    { id: 1, type: 'liquidity', severity: 'high', message: 'Liquidity removed: 120K USDC', detail: 'DEX Pool #2', time: '14:32:18', icon: 'droplet' },
    { id: 2, type: 'whale', severity: 'medium', message: 'Whale accumulation detected', detail: '+2.3% supply', time: '14:32:15', icon: 'whale' },
    { id: 3, type: 'mev', severity: 'high', message: 'Sandwich attack in progress', detail: '$47K impacted', time: '14:32:12', icon: 'bot' },
    { id: 4, type: 'contract', severity: 'low', message: 'Contract verification passed', detail: 'All checks clear', time: '14:32:08', icon: 'shield' },
    { id: 5, type: 'holders', severity: 'medium', message: 'Unusual holder pattern', detail: '12 wallets coordinated', time: '14:31:54', icon: 'users' },
    { id: 6, type: 'price', severity: 'high', message: 'Price manipulation detected', detail: '-8.2% in 30s', time: '14:31:47', icon: 'dollar' },
    { id: 7, type: 'liquidity', severity: 'critical', message: 'Major LP exit initiated', detail: '$1.2M unlocking', time: '14:31:42', icon: 'flame' },
    { id: 8, type: 'mev', severity: 'medium', message: 'MEV bot cluster active', detail: '5 bots detected', time: '14:31:35', icon: 'bot' },
  ]);

  // Overall risk score calculation
  const [riskScore, setRiskScore] = useState(42);
  const [riskTrend, setRiskTrend] = useState('increasing');
  
  // Simulate new risk events
  useEffect(() => {
    const interval = setInterval(() => {
      const eventTypes = [
        { type: 'liquidity', severity: 'high', message: 'LP position changed', detail: '${Math.floor(Math.random() * 500)}K moved', icon: 'droplet' },
        { type: 'whale', severity: 'medium', message: 'Whale wallet active', detail: `${(Math.random() * 5).toFixed(1)}% supply`, icon: 'whale' },
        { type: 'mev', severity: 'high', message: 'MEV attack detected', detail: `$${Math.floor(Math.random() * 100)}K targeted`, icon: 'bot' },
        { type: 'holders', severity: 'low', message: 'New holders surge', detail: `+${Math.floor(Math.random() * 50)} wallets`, icon: 'users' },
        { type: 'contract', severity: 'critical', message: 'Contract anomaly', detail: 'Unexpected function call', icon: 'flame' },
        { type: 'price', severity: 'medium', message: 'Price volatility spike', detail: `±${(Math.random() * 10).toFixed(1)}% swing`, icon: 'dollar' },
      ];
      
      const newEvent = eventTypes[Math.floor(Math.random() * eventTypes.length)];
      const now = new Date();
      const timeString = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;
      
      setRiskEvents(prev => [{
        id: Date.now(),
        ...newEvent,
        time: timeString
      }, ...prev].slice(0, 50)); // Keep last 50 events
      
      // Update risk score
      setRiskScore(prev => {
        const change = (Math.random() - 0.5) * 10;
        return Math.max(0, Math.min(100, prev + change));
      });
    }, 3000 + Math.random() * 4000); // Random interval between 3-7 seconds
    
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll risk feed
  useEffect(() => {
    if (riskFeedRef.current) {
      riskFeedRef.current.scrollTop = 0;
    }
  }, [riskEvents]);

  // Simulated anomaly trades data
  const [anomalyTrades] = useState([
    { hash: "0xa1b2c3d4e5f6", value: "2.1M", risk: "HIGH", date: "14:32", type: "Flash Loan", wallet: "0x1a2b...3c4d" },
    { hash: "0xf6e5d4c3b2a1", value: "850K", risk: "MED", date: "14:28", type: "Large Transfer", wallet: "0x5e6f...7g8h" },
    { hash: "0x123456789abc", value: "15K", risk: "LOW", date: "14:25", type: "MEV Bot", wallet: "0x9i0j...1k2l" },
    { hash: "0x987654321def", value: "1.5M", risk: "HIGH", date: "14:21", type: "Whale Move", wallet: "0x3m4n...5o6p" },
    { hash: "0x456789abcdef", value: "620K", risk: "MED", date: "14:18", type: "Arbitrage", wallet: "0x7q8r...9s0t" },
    { hash: "0xabcdef123456", value: "1.3M", risk: "MED", date: "14:15", type: "Sandwich", wallet: "0x1u2v...3w4x" },
    { hash: "0xdef123456789", value: "340K", risk: "LOW", date: "14:12", type: "DeFi Swap", wallet: "0x5y6z...7a8b" },
    { hash: "0x789abcdef123", value: "2.8M", risk: "HIGH", date: "14:09", type: "Flash Loan", wallet: "0x9c0d...1e2f" },
    { hash: "0xbcdef123456a", value: "120K", risk: "LOW", date: "14:06", type: "Normal Trade", wallet: "0x3g4h...5i6j" },
    { hash: "0xef123456789b", value: "3.1M", risk: "HIGH", date: "14:03", type: "Whale Exit", wallet: "0x7k8l...9m0n" },
    { hash: "0x23456789abcd", value: "450K", risk: "MED", date: "14:00", type: "Bot Trade", wallet: "0x1o2p...3q4r" },
    { hash: "0x56789abcdef1", value: "890K", risk: "MED", date: "13:57", type: "Arbitrage", wallet: "0x5s6t...7u8v" },
    { hash: "0x9abcdef12345", value: "75K", risk: "LOW", date: "13:54", type: "Swap", wallet: "0x9w0x...1y2z" },
    { hash: "0xcdef123456ab", value: "1.7M", risk: "HIGH", date: "13:51", type: "Coordinated Buy", wallet: "0x3a4b...5c6d" },
    { hash: "0xef123456789c", value: "290K", risk: "LOW", date: "13:48", type: "MEV Bot", wallet: "0x7e8f...9g0h" },
    { hash: "0x34567890bcde", value: "1.2M", risk: "HIGH", date: "13:45", type: "Flash Loan", wallet: "0x1i2j...3k4l" },
    { hash: "0x67890bcdef12", value: "560K", risk: "MED", date: "13:42", type: "Large Transfer", wallet: "0x5m6n...7o8p" },
    { hash: "0x0bcdef123456", value: "2.3M", risk: "HIGH", date: "13:39", type: "Whale Move", wallet: "0x9q0r...1s2t" }
  ]);

  const totalPages = Math.ceil(anomalyTrades.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTrades = anomalyTrades.slice(startIndex, startIndex + itemsPerPage);

  const RiskIcon = ({ risk }) => {
    switch (risk) {
      case 'HIGH':
        return <AlertTriangle className={`${styles.riskIcon} ${styles.riskHigh}`} />;
      case 'MED':
        return <AlertCircle className={`${styles.riskIcon} ${styles.riskMed}`} />;
      case 'LOW':
        return <CheckCircle className={`${styles.riskIcon} ${styles.riskLow}`} />;
      default:
        return null;
    }
  };

  const EventIcon = ({ type, severity }) => {
    const iconClass = `${styles.eventIcon} ${styles[`severity${severity.charAt(0).toUpperCase() + severity.slice(1)}`]}`;
    
    switch (type) {
      case 'droplet':
        return <Droplet className={iconClass} />;
      case 'whale':
        return <Users className={iconClass} />;
      case 'bot':
        return <Bot className={iconClass} />;
      case 'shield':
        return <Shield className={iconClass} />;
      case 'users':
        return <Users className={iconClass} />;
      case 'dollar':
        return <DollarSign className={iconClass} />;
      case 'flame':
        return <Flame className={iconClass} />;
      default:
        return <AlertCircle className={iconClass} />;
    }
  };

  const getRiskLevel = (score) => {
    if (score > 70) return { level: 'CRITICAL', class: 'critical' };
    if (score > 50) return { level: 'HIGH', class: 'high' };
    if (score > 30) return { level: 'MEDIUM', class: 'medium' };
    return { level: 'LOW', class: 'low' };
  };

  const currentRisk = getRiskLevel(riskScore);

  return (
    <div className={styles.container}>
      {/* Left Side - Anomaly Trades */}
      <div className={styles.anomalySection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            Anomaly Trades
            <span className={styles.liveIndicator}>
              <span className={styles.liveDot} />
              LIVE
            </span>
          </h2>
          <div className={styles.sectionActions}>
            <button className={styles.actionButton}>
              <Filter className={styles.actionIcon} />
            </button>
            <button className={styles.actionButton}>
              <Search className={styles.actionIcon} />
            </button>
          </div>
        </div>

        <div className={styles.tradesTable}>
          <div className={styles.tableHeader}>
            <span>Type</span>
            <span>Value</span>
            <span>Time</span>
            <span></span>
          </div>
          <div className={styles.tableBody}>
            {paginatedTrades.map((anomaly, idx) => (
              <div key={idx} className={styles.tradeRow}>
                <div className={styles.tradeType}>
                  <RiskIcon risk={anomaly.risk} />
                  <span>{anomaly.type}</span>
                </div>
                <span className={styles.tradeValue}>${anomaly.value}</span>
                <span className={styles.tradeTime}>{anomaly.date}</span>
                <button className={styles.inspectButton}>
                  <Activity className={styles.inspectIcon} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.pagination}>
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className={styles.pageButton}
          >
            <ChevronLeft />
          </button>
          <span className={styles.pageText}>{currentPage} / {totalPages}</span>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className={styles.pageButton}
          >
            <ChevronRight />
          </button>
        </div>
      </div>

      {/* Right Side - Risk Analysis Feed */}
      <div className={styles.riskAnalysisContainer}>
        {/* Risk Score Header */}
        <div className={styles.riskScoreHeader}>
          <div className={styles.riskScoreMain}>
            <div className={styles.riskScoreLabel}>RISK ASSESSMENT</div>
            <div className={styles.riskScoreValue}>
              <span className={`${styles.scoreNumber} ${styles[currentRisk.class]}`}>
                {riskScore.toFixed(0)}
              </span>
              <span className={styles.scoreMax}>/100</span>
            </div>
            <div className={`${styles.riskLevel} ${styles[currentRisk.class]}`}>
              {currentRisk.level}
              {riskTrend === 'increasing' ? 
                <TrendingUp className={styles.trendIndicator} /> : 
                <TrendingDown className={styles.trendIndicator} />
              }
            </div>
          </div>
          
          <div className={styles.quickStats}>
            <div className={styles.quickStat}>
              <span className={styles.statLabel}>24h Events</span>
              <span className={styles.statValue}>247</span>
            </div>
            <div className={styles.quickStat}>
              <span className={styles.statLabel}>Critical</span>
              <span className={`${styles.statValue} ${styles.critical}`}>12</span>
            </div>
            <div className={styles.quickStat}>
              <span className={styles.statLabel}>Monitoring</span>
              <span className={`${styles.statValue} ${styles.active}`}>ACTIVE</span>
            </div>
          </div>
        </div>

        {/* Risk Event Feed */}
        <div className={styles.riskFeedSection}>
          <div className={styles.feedHeader}>
            <h3 className={styles.feedTitle}>
              Risk Event Stream
              <span className={styles.feedIndicator}>
                <Zap className={styles.feedPulse} />
                SCANNING
              </span>
            </h3>
            <div className={styles.feedActions}>
              <button className={styles.feedAction}>
                <Eye className={styles.actionIcon} />
              </button>
              <button className={styles.feedAction}>
                <Clock className={styles.actionIcon} />
              </button>
            </div>
          </div>

          <div className={styles.riskFeed} ref={riskFeedRef}>
            {riskEvents.map((event) => (
              <div 
                key={event.id} 
                className={`${styles.riskEvent} ${styles[`severity${event.severity.charAt(0).toUpperCase() + event.severity.slice(1)}`]} ${styles.eventNew}`}
              >
                <div className={styles.eventTime}>{event.time}</div>
                <div className={styles.eventContent}>
                  <EventIcon type={event.icon} severity={event.severity} />
                  <div className={styles.eventDetails}>
                    <div className={styles.eventMessage}>{event.message}</div>
                    <div className={styles.eventDetail}>{event.detail}</div>
                  </div>
                </div>
                <div className={`${styles.eventSeverity} ${styles[event.severity]}`}>
                  {event.severity.toUpperCase()}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Critical Alerts Summary */}
        <div className={styles.alertsSummary}>
          <div className={styles.alertItem}>
            <Lock className={styles.alertIcon} />
            <span className={styles.alertText}>Liquidity unlock in 12 days</span>
          </div>
          <div className={styles.alertItem}>
            <Users className={styles.alertIcon} />
            <span className={styles.alertText}>Top 2 wallets: 50% supply</span>
          </div>
          <div className={styles.alertItem}>
            <Bot className={styles.alertIcon} />
            <span className={styles.alertText}>MEV dominance: 23%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveMonitor;